import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBookingWizard } from 'core/contexts/bookingWizard';
import { useCurrency } from 'core/contexts/currency';
import { alpha, Box, Button, Divider, FormHelperText, Grid2, Typography, useMediaQuery } from '@mui/material';
import { Field, Form, FormSpy } from 'react-final-form';
import { AlertDialog, BottomSheet, DurationDay, DurationHour, DurationMinutes, ExperienceDatePicker, ExperienceDateSelector, NumberInput, PriceDetails, SelectComponent, Switch, UnitSelector, useDebounce } from '@utriper/core-ui';
import { Container, Label } from '../../styles';
import ResourceGrid from './components/ResourceGrid';
import DateSelector from './components/DateSelector';
import { AvailabilityService } from 'core/api/services/availability';
import { PricingService } from 'core/api/services/pricing';
import { formatCurrencyWithDecimals } from '@utriper/core';

const FormContent = ({ form, ui, data }) => {
    // FORM CONTENT PROPERTIES
    const { formApi, values, submitFailed, errors, handleSubmit, handleClose } = form;
    const { isMobile, language, labelNext, loadingAvailability, setLoadingAvailability, loadingPricing, setLoadingPricing } = ui;
    const { contractId, targetCurrency, slots, setSlots, pricing, setPricing, overridePrice, setOverridePrice } = data;

    // OPEN STATES
    const [openUnits, setOpenUnits] = useState(false);
    const [openDetails, setOpenDetails] = useState(false);

    // INPUT STATES
    const [dateRange, setDateRange] = useState({
        start: null,
        end: null,
    });

    // EXPERIENCE AVAILABILITY COMPONENT
    const [datePickerValue, setDatePickerValue] = useState(new Date());

    const overridePriceDebounce = useDebounce(overridePrice, 800);
    const experienceDetailOptions = useMemo(
        () =>
            values?.experienceDetails?.map((expDetail) => ({
                id: expDetail?.id,
                name: expDetail?.name || '',
                ...expDetail,
            })),
        [values?.experienceDetails]
    );
    const totalQuantity = useMemo(() => {
        const units = Array.isArray(values?.formData?.units) ? values?.formData?.units : [];

        return units?.reduce((acc, item) => acc + Number(item?.quantity || 0), 0);
    }, [values?.formData?.units]);

    const overrideAvailability = useMemo(() => values?.formData?.overrideData?.enabled, [values?.formData?.overrideData?.enabled]);
    const disabledNext = useMemo(() => loadingAvailability || loadingPricing || !Number.isFinite(pricing?.payNow) || Object.values(errors)?.length > 0, [errors, loadingAvailability, loadingPricing, pricing?.payNow]);

    // HANDLE CHANGING
    const handleResource = (newValues) => {
        formApi.change('formData.resourcesSelected', newValues);
    };

    const handleOption = (newExpDetail) => {
        const oldExpDetailId = values?.formData?.experienceDetailId;
        if (oldExpDetailId === newExpDetail?.id) return;

        if (!loadingPricing && pricing?.payTotal) {
            setPricing(null);
        }

        formApi.batch(() => {
            formApi.change('formData.experienceDetailId', newExpDetail.id);
            formApi.change('formData.experienceDetailSelected', newExpDetail);
            formApi.change('formData.selectedSlot', null);
        });
    };

    const handleUnit = ({ units = [] }) => {
        if (!loadingPricing && pricing?.payTotal) {
            setPricing(null);
        }

        formApi.batch(() => {
            formApi.change('formData.units', units);
            formApi.change('formData.selectedSlot', null);
        });
        setOpenUnits(false);
    };

    const handleSlot = (newSlot) => {
        if (!loadingPricing && pricing?.payTotal) {
            setPricing(null);
        }

        formApi.change('formData.selectedSlot', { ...newSlot });
    };

    const handleOverride = (event) => {
        const checked = event?.target?.checked || false;
        formApi.batch(() => {
            formApi.change('formData.overrideData.enabled', checked);
            formApi.change('formData.selectedSlot', null);
        });
    };

    // API CALLS

    // GET AVAILABILITY
    useEffect(() => {
        const getAvailability = async () => {
            try {
                setLoadingAvailability(true);
                const slots = await AvailabilityService.getAvailability({
                    experienceId: values?.experienceId,
                    experienceDetailId: values?.formData?.experienceDetailId,
                    fromDateTime: new Date(dateRange.start),
                    toDateTime: new Date(dateRange.end),
                    units: values?.formData?.units,
                    code: contractId ? contractId.toString(16) : null,
                    targetCurrency,
                });

                setSlots([...slots]);
            } catch (error) {
                // TODO: ADD ERROR HANDLER
            } finally {
                setLoadingAvailability(false);
            }
        };

        if (totalQuantity > 0 && values?.formData?.experienceDetailId && Date.parse(dateRange.start) && Date.parse(dateRange.end)) {
            getAvailability();
        }
    }, [contractId, dateRange.end, dateRange.start, setLoadingAvailability, setSlots, targetCurrency, totalQuantity, values?.experienceId, values?.formData?.experienceDetailId, values?.formData?.units]);

    // PRICING QUOTE
    useEffect(() => {
        const quote = async () => {
            try {
                setLoadingPricing(true);

                const item = {
                    experienceId: values?.experienceId,
                    experienceDetailId: values?.formData?.experienceDetailId,
                    availabilityId: values?.formData?.selectedSlot?.id || null,
                    bundleId: values?.bundleId,
                    units: values?.formData?.units || [],
                };

                if (overrideAvailability) {
                    item.overridePrice = overridePriceDebounce || 0;
                    item.overrideCurrency = targetCurrency;
                }

                const pricing = await PricingService.quote({ item, contractId, overrideAvailability, targetCurrency });

                setPricing(pricing);
            } catch (error) {
                // TODO: ADD ERROR HANDLER
            } finally {
                setLoadingPricing(false);
            }
        };

        const allowOpenDateBooking = values?.allowOpenDateBooking;
        const hasSelectedSlot = Number.isInteger(values?.formData?.selectedSlot?.id);
        const canCall = overrideAvailability ? true : !allowOpenDateBooking && !hasSelectedSlot ? false : true;

        if (totalQuantity > 0 && values?.formData?.experienceDetailId && canCall) {
            quote();
        }
    }, [contractId, overrideAvailability, overridePriceDebounce, targetCurrency, setLoadingPricing, setPricing, totalQuantity, values?.allowOpenDateBooking, values?.bundleId, values?.experienceId, values?.formData?.experienceDetailId, values?.formData?.selectedSlot?.id, values?.formData?.units]);

    return (
        <form id="activity-form" style={{ width: '100%' }}>
            <Box pt={2}>
                {/* TITLE (ONLY SHOW WHEN IS DESKTOP) */}
                {!isMobile && (
                    <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                        <Typography fontSize={20} fontWeight={900} sx={{ color: '#131313' }} noWrap>
                            Agregar Experiencia
                            {/* / ACTUALIZAR */}
                        </Typography>
                        <Typography fontSize={16} fontWeight={500} sx={{ color: '#131313bf' }}>
                            Agrega una experiencia al checkout
                            {/* / ACTUALIZAR */}
                        </Typography>
                    </Grid2>
                )}

                {/* FORM */}
                <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                    {/* RESOURCES */}
                    {values?.resourceCategories?.length > 0 && <ResourceGrid categories={values.resourceCategories} resourcesSelected={values?.formData?.resourcesSelected} handleResource={handleResource} />}

                    {/* OPTION */}
                    <Container>
                        <Label>Opción de la Experiencia</Label>
                        {values?.bundleId ? <Typography fontSize={16} fontWeight={500} sx={{ color: '#131313' }}>{`${values?.name} (${values?.bundleName})`}</Typography> : <Field name="formData.experienceDetailId" component={SelectComponent} inputHeight={44} values={experienceDetailOptions} onChange={handleOption} label={'Seleccionar Opción'} hasError={submitFailed && errors?.experienceDetailId} errorMessage={submitFailed && errors?.experienceDetailId ? errors.experienceDetailId : null} />}
                    </Container>

                    {/* UNITS */}
                    <Container>
                        <Label>Cantidad de Personas</Label>
                        <UnitSelector open={openUnits} units={values?.formData?.units} handleOpen={(value) => setOpenUnits(value)} handleUnit={handleUnit} />
                    </Container>

                    {/* DATE */}
                    <Container>
                        <Label>Fecha de la Actividad</Label>
                        {overrideAvailability ? (
                            <Field
                                name="formData.overrideData.date"
                                render={({ input, ...rest }) => (
                                    <DateSelector
                                        value={input?.value}
                                        onChange={(date) => {
                                            if (!Date.parse(date)) return;

                                            formApi.change('formData.overrideData.date', date);
                                        }}
                                        placeholder="Selecciona una fecha"
                                        {...rest}
                                    />
                                )}
                            />
                        ) : isMobile ? (
                            <ExperienceDateSelector
                                slots={slots}
                                experience={values}
                                selectedSlot={values?.formData?.selectedSlot}
                                setSelectedSlot={handleSlot}
                                setDateRange={setDateRange}
                                loading={loadingAvailability}
                                // disabled={disabled}
                                placeholder="Selecciona una fecha"
                                hasError={submitFailed && errors?.date}
                            />
                        ) : (
                            <ExperienceDatePicker
                                datePicker={datePickerValue}
                                setDatePicker={setDatePickerValue}
                                setVisibleRange={setDateRange}
                                slots={slots}
                                experience={values}
                                selectedSlot={values?.formData?.selectedSlot}
                                setSelectedSlot={handleSlot}
                                showPrice={false}
                                showTotalPrice={false}
                                showLoading={loadingAvailability}
                                // disabled={disabled}
                                disabledLabel={'Debes seleccionar al menos un tipo de pasajero!'} // REVISAR
                            />
                        )}
                        {submitFailed && errors?.date && <FormHelperText error>{errors.date}</FormHelperText>}
                    </Container>

                    {/* OVERRIDE FIELDS */}
                    {overrideAvailability && (
                        <Fragment>
                            {/* DURATION */}
                            <Container>
                                <Label>Duración</Label>
                                <Container direction="row" flexWrap="nowrap">
                                    <Grid2 size={4}>
                                        <DurationDay
                                            name="formData.overrideData.duration.days"
                                            value={values?.formData?.overrideData?.duration?.days}
                                            onFieldChange={(value) => {
                                                formApi.change('formData.overrideData.duration.days', value);
                                            }}
                                            label={'Días'}
                                        />
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <DurationHour
                                            name="formData.overrideData.duration.hours"
                                            value={values?.formData?.overrideData?.duration?.hours}
                                            onFieldChange={(value) => {
                                                formApi.change('formData.overrideData.duration.hours', value);
                                            }}
                                            label={'Horas'}
                                        />
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <DurationMinutes
                                            name="formData.overrideData.duration.minutes"
                                            value={values?.formData?.overrideData?.duration?.minutes}
                                            onFieldChange={(value) => {
                                                formApi.change('formData.overrideData.duration.minutes', value);
                                            }}
                                            label={'Minutos'}
                                        />
                                    </Grid2>
                                </Container>
                            </Container>

                            {/* PRICE */}
                            <Container>
                                <Label>Precio Total</Label>
                                <Field name="formData.overrideData.price" component={NumberInput} label={''} placeholder="Ingresa el precio total" />
                            </Container>
                        </Fragment>
                    )}

                    {/* OVERRIDE SWITCH */}
                    <Container>
                        <Field
                            type="checkbox"
                            name="formData.overrideData.enabled"
                            component={Switch}
                            label="Sobreescribir disponibilidad"
                            input={{
                                checked: values?.formData?.overrideData?.enabled,
                                onChange: handleOverride,
                            }}
                        />
                    </Container>

                    {/* PRICING */}
                    {Number.isFinite(pricing?.payNow) && (
                        <Fragment>
                            <Divider flexItem sx={{ borderColor: alpha('#131313', 0.25), mt: 1 }} />

                            <Grid2 container size={12} spacing={1} direction="column" mt={1}>
                                <Grid2 container justifyContent="space-between">
                                    <Typography fontSize="16px" fontWeight={500} sx={{ color: alpha('#131313', 0.75) }}>
                                        Total:
                                    </Typography>
                                    <Typography fontSize="16px" fontWeight={700} sx={{ color: '#131313' }}>
                                        {formatCurrencyWithDecimals(pricing?.payTotal, targetCurrency, language, 0)}
                                    </Typography>
                                </Grid2>

                                {pricing.payTotal > 0 && (
                                    <Button
                                        sx={{
                                            width: '100%',
                                            justifyContent: 'flex-end',
                                            color: alpha('#131313', 0.75),
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            p: 0,
                                            minWidth: 0,
                                            textDecoration: 'underline',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                                textDecoration: 'underline',
                                            },
                                            '&:active': {
                                                backgroundColor: 'transparent',
                                                textDecoration: 'underline',
                                            },
                                            '&:focus': {
                                                backgroundColor: 'transparent',
                                                textDecoration: 'underline',
                                            },
                                        }}
                                        variant="text"
                                        component="button"
                                        onClick={() => setOpenDetails(true)}
                                    >
                                        Ver detalles
                                    </Button>
                                )}
                            </Grid2>
                        </Fragment>
                    )}

                    {/* BUTTONS */}
                    <Grid2 container size={12} spacing={1} justifyContent="flex-end" alignItems="center">
                        <Button
                            sx={{
                                border: 'solid 1px #13131340',
                                color: '#131313',
                                textTransform: 'none',
                                fontWeight: 500,
                            }}
                            onClick={(e) => {
                                handleClose();
                                formApi.reset(e);
                            }}
                            variant="outlined"
                        >
                            Cancelar
                        </Button>
                        <Button
                            sx={{
                                backgroundColor: 'var(--color-utriper-blue)',
                                textTransform: 'none',
                                fontWeight: 500,
                            }}
                            type="submit"
                            disabled={disabledNext}
                            onClick={(e) => {
                                return handleSubmit(e);
                            }}
                            variant="contained"
                        >
                            {labelNext || 'Agregar'}
                        </Button>
                    </Grid2>
                </Grid2>
            </Box>

            <FormSpy
                subscription={{ values: true }}
                onChange={({ values }) => {
                    const { formData } = values;

                    if (formData?.overrideData?.enabled && formData?.overrideData?.price) {
                        setOverridePrice(formData.overrideData.price);
                    }
                }}
            />

            <AlertDialog
                open={openDetails && pricing?.payTotal}
                handleClose={() => {
                    setOpenDetails(false);
                }}
                hideTitle
                hideSubmit
                labelBack={'Cerrar'}
                maxWidth="sm"
                fullWidth
                dialogContent={<PriceDetails unitPricing={pricing?.unitPricing} currency={targetCurrency} totalPrice={pricing?.payTotal} experience={values} date={overrideAvailability && values?.formData?.overrideData?.date ? new Date(values.formData.overrideData.date) : values?.formData?.selectedSlot?.startUTC ? new Date(values.formData.selectedSlot.startUTC) : null} />}
            />
        </form>
    );
};

const ActivityForm = ({ open = false, initialItem, handleSubmit = () => {}, handleClose = () => {}, labelNext }) => {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { contractId, validateCheckoutItem } = useBookingWizard();
    const { currency } = useCurrency();
    const { i18n } = useTranslation();
    const [slots, setSlots] = useState([]);
    const [pricing, setPricing] = useState(initialItem?.pricing ? { ...initialItem.pricing } : null);
    const [overridePrice, setOverridePrice] = useState(initialItem?.formData?.overrideData?.price || null);

    // LOADING STATES
    const [loadingAvailability, setLoadingAvailability] = useState(false);
    const [loadingPricing, setLoadingPricing] = useState(false);

    if (!open) {
        return null;
    }

    const resetAllStates = () => {
        setSlots([]);
        setPricing(null);
        setOverridePrice(null);
    };

    const handleToggle = () => {
        if (open) {
            handleClose();
            resetAllStates();
        }
    };

    const validateForm = (values) => {
        return validateCheckoutItem({ ...values, pricing });
    };

    const handleSubmitForm = (values) => {
        return handleSubmit({ ...values, pricing });
    };

    return (
        <Form
            onSubmit={handleSubmitForm}
            validate={validateForm}
            initialValues={{ ...initialItem }}
            render={({ handleSubmit, values, submitFailed, errors, form }) => {
                const formContentProps = {
                    form: {
                        formApi: form,
                        values,
                        submitFailed,
                        errors,
                        handleSubmit,
                        handleClose: () => {
                            handleClose();
                            resetAllStates();
                        },
                    },
                    ui: {
                        isMobile,
                        language: i18n.language,
                        labelNext,
                        loadingAvailability,
                        setLoadingAvailability,
                        loadingPricing,
                        setLoadingPricing,
                    },
                    data: {
                        contractId,
                        targetCurrency: currency,
                        slots,
                        setSlots,
                        pricing,
                        setPricing,
                        overridePrice,
                        setOverridePrice,
                    },
                };

                return isMobile ? (
                    <BottomSheet
                        open={open}
                        title="Agregar Experiencia" // REVISAR
                        hasActions
                        actionBack={{
                            label: 'Cancelar',
                            onClick: (e) => {
                                handleClose();
                                resetAllStates();
                                form.reset(e);
                            },
                        }}
                        actionNext={{
                            label: 'Agregar', // / Actualizar
                            type: 'submit',
                            onClick: (e) => {
                                return handleSubmit(e);
                            },
                        }}
                        handleToggle={handleToggle}
                    >
                        <FormContent {...formContentProps} />
                    </BottomSheet>
                ) : (
                    <AlertDialog
                        open={open}
                        handleClose={() => {
                            handleClose();
                            resetAllStates();
                        }}
                        hideTitle
                        hideActions
                        fullWidth
                        paperProps={{
                            sx: {
                                minWidth: '370px',
                                maxWidth: '480px',
                                width: 'fit-content',
                            },
                        }}
                        dialogContent={<FormContent {...formContentProps} />}
                    />
                );
            }}
        />
    );
};

export default ActivityForm;
